import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { TONE_BG, TONE_PILL } from "@/lib/widget-status";
import type { InvoiceDisplayStatus } from "@/lib/invoice-list";

/**
 * Status på /faktura i StatusBadge-mønsteret (prikk + pille). Betalt og
 * Forfalt bruker KPI-paletten; Sendt, Utkast og Kreditnota har ingen tone
 * i paletten og bruker nøytrale tokens.
 */
const PILL: Record<InvoiceDisplayStatus, { label: string; pill: string; dot: string }> = {
  paid: { label: "Betalt", pill: TONE_PILL.reached, dot: TONE_BG.reached },
  credited: { label: "Kreditert", pill: "bg-secondary text-foreground", dot: "bg-muted-foreground" },
  overdue: { label: "Forfalt", pill: TONE_PILL.miss, dot: TONE_BG.miss },
  sent: { label: "Sendt", pill: "bg-primary/10 text-primary", dot: "bg-primary" },
  draft: { label: "Utkast", pill: "bg-muted text-muted-foreground", dot: "bg-muted-foreground" },
  credit_note: { label: "Kreditnota", pill: "bg-secondary text-foreground", dot: "bg-foreground" },
  other: { label: "Ukjent", pill: "bg-muted text-muted-foreground", dot: "bg-muted-foreground" },
};

interface InvoiceStatusPillProps {
  status: InvoiceDisplayStatus;
  /** Dager forfalt, vises som «Forfalt · 27 d». */
  daysOverdue?: number | null;
  /** Når saldoen gikk til null i PO, vises ved hover på «Betalt» og «Kreditert». */
  paidDate?: string | null;
}

export function InvoiceStatusPill({ status, daysOverdue, paidDate }: InvoiceStatusPillProps) {
  const p = PILL[status];
  const settled = (status === "paid" || status === "credited") && !!paidDate;
  const pill = (
    <span
      tabIndex={settled ? 0 : undefined}
      className={cn(
        "inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        p.pill,
      )}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", p.dot)} />
      {p.label}
      {status === "overdue" && daysOverdue != null && ` · ${daysOverdue} d`}
    </span>
  );
  if (!settled) return pill;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent>
          {status === "credited" ? "Gjort opp med kreditnota" : "Betalt"} {formatDate(paidDate)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
